import { Languages } from 'lucide-react';

import { Button } from '@shared';
import { getLocale, reloadForLocale, setLocale, t } from '@/i18n';

import { hasUnsavedComposition } from './unsaved-composition';

import type { SupportedLocale } from '@/i18n';

/**
 * 헤더 언어 토글 (ko ⇄ en).
 *
 * 배경: getLocale() 이 navigator.language 만 보던 시절, 영어 브라우저를 쓰는 한국 사용자는
 * 한글로 바꿀 수단이 아예 없었다. 이 토글이 localStorage('locale') 에 명시 선택을 저장하고,
 * getLocale() 이 그 값을 navigator 보다 우선한다.
 *
 * 반영 방식은 reload: t() 는 훅이 아닌 순수 함수라 88개 호출부가 리렌더를 구독하지 않고,
 * 그중 일부(journal/labels.ts 등)는 React 트리 밖에서 t() 를 부르므로 상태 기반으로는
 * 부분 번역이 남는다. 저빈도(세션당 0~1회) 행위라 reload 로 전 화면 일관 반영을 택했다.
 *
 * reload 는 URL·localStorage 밖의 상태를 버리므로, 작성 중인 본문이 있으면 먼저 확인을 받는다
 * (헤더는 글쓰기 화면에도 떠 있다 — hasUnsavedComposition 주석 참고).
 *
 * UI 규격은 헤더의 기존 아이콘+라벨 버튼(마이/로그아웃)과 동일 — 신규 레이아웃 없음.
 */
export const LanguageToggle = () => {
  // TRAD-2723 — 분기를 `getLocale() === 'ko' ? 'ko' : 'en'` 으로 잡는다(종전엔
  // `=== 'en' ? 'en' : 'ko'`). 지원하지 않는 3번째 로케일(카탈로그가 늘면 벌어질 상황)이
  // 오면 **"현재 언어 = 영어"로 표시**한다 — 표시 문구는 영어 폴백이 맞다는 팀 결정
  // (`getIntlLocale` 숫자·날짜 포맷과는 다른 판단, TRAD-2723 참고). 종전 분기는 "en 이
  // 아니면 전부 한국어"라 미지 로케일에 "현재 언어 = 한국어"라고 잘못 자기소개했다.
  // ko/en 두 로케일의 결과는 이 변경으로 바뀌지 않는다(테스트 양방향 고정).
  const current = getLocale() === 'ko' ? 'ko' : 'en';
  const next: SupportedLocale = current === 'ko' ? 'en' : 'ko';

  const handleToggle = () => {
    // 작성 중인 글이 조용히 사라지는 것만은 막는다. 확인은 실제로 작성 중일 때만 뜬다.
    if (hasUnsavedComposition(document) && !window.confirm(t('header.language.confirm_discard'))) {
      return;
    }

    // 저장에 실패하면(프라이빗 모드 등) reload 해봐야 원래 언어로 돌아올 뿐이라 건너뛴다.
    // TRAD-3921 — `/en/…`(주소가 언어를 정한다)에서 한국어를 고르면 제자리 reload 가 아니라 한국어 주소로 간다(reloadForLocale).
    if (setLocale(next)) {
      reloadForLocale(next);
    }
  };

  return (
    // 🔴 TRAD-2795 배치4 — LanguageToggle는 props 없는 완전 닫힌 컴포넌트라 렌더 경로가
    // 이 버튼 하나뿐이다(Switch처럼 props spread가 아니라 static testid로 충분).
    // aria-label은 그대로 유지 — testid는 병기이지 대체가 아니다.
    <Button
      type="button"
      variant="ghost"
      onClick={handleToggle}
      data-testid="language-toggle"
      aria-label={t('header.language.label')}
      title={t(next === 'ko' ? 'header.language.ko' : 'header.language.en')}
      className="h-auto gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
    >
      <Languages size={14} />
      <span className="hidden sm:inline">{t(current === 'ko' ? 'header.language.ko' : 'header.language.en')}</span>
    </Button>
  );
};
